import React, { useCallback, useEffect, useState } from 'react';
import { ShieldAlert, Loader2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { autoBlock } from '../../services/superuserApi';

// Alvos de toque ≥ 44 px e texto ≥ 12 px: este painel é usado no telemóvel, a 390 px.
const TAP = 44;

const apiMessage = (e, fallback) => e?.response?.data?.message || e?.message || fallback;

const fmtDate = (value) => (value
  ? new Date(value).toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric' })
  : '—');

/**
 * Bloqueio automático por vencimento.
 *
 * O que o dono faz aqui, e por esta ordem: vê QUEM SERIA BLOQUEADO HOJE (a lista é calculada
 * sem tocar em ninguém), escolhe a carência, e só depois liga o interruptor. Duas travas até
 * haver um bloqueio a sério: `Ligado` e `Só simular` (esta segunda vem ligada por omissão).
 *
 * O ecrã diz sempre a regra em vigor, incluindo a carência por omissão do servidor
 * (Billing:AutoBlock:GraceDays = 7): uma «carência configurável» que não se lê no ecrã não é
 * configurável, é uma surpresa. A plataforma e as instâncias inactivas aparecem na lista como
 * excluídas, com o motivo — o dono vê que o sistema sabe quem NÃO toca.
 */
const AutoBlockPanel = () => {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [enabled, setEnabled] = useState(false);
  const [simulateOnly, setSimulateOnly] = useState(true);
  const [graceDays, setGraceDays] = useState(7);

  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [message, setMessage] = useState(null);
  const [runMessage, setRunMessage] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [problem, setProblem] = useState(null);

  const apply = (data) => {
    setState(data);
    setEnabled(!!data?.enabled);
    setSimulateOnly(!!data?.simulateOnly);
    setGraceDays(Number.isFinite(data?.graceDays) ? data.graceDays : 7);
    setCandidates(Array.isArray(data?.candidates) ? data.candidates : []);
  };

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await autoBlock.get();
      apply(res.data);
    } catch (e) {
      setLoadError(apiMessage(e, 'Não foi possível ler a definição do bloqueio automático.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setProblem(null);
    if (!Number.isFinite(graceDays) || graceDays < 0 || graceDays > 365) {
      setProblem('A carência tem de estar entre 0 e 365 dias.');
      return;
    }

    setSaving(true);
    try {
      const res = await autoBlock.save({ enabled, graceDays, simulateOnly });
      setMessage(res.data?.message || 'Definições guardadas.');
      setCandidates(Array.isArray(res.data?.candidates) ? res.data.candidates : []);
      setState((prev) => ({ ...prev, ...res.data }));
      toast.success(res.data?.message || 'Definições guardadas.');
    } catch (e) {
      setProblem(apiMessage(e, 'Não foi possível guardar as definições.'));
    } finally {
      setSaving(false);
    }
  };

  const preview = async () => {
    setProblem(null);
    setConfirming(false);
    setRunning(true);
    try {
      // apply=false: o servidor avalia e devolve a lista, sem tocar em ninguém.
      const res = await autoBlock.run(false);
      setCandidates(Array.isArray(res.data?.candidates) ? res.data.candidates : []);
      setRunMessage(res.data?.message || 'Simulação: nada foi alterado.');
    } catch (e) {
      setProblem(apiMessage(e, 'Não foi possível simular o bloqueio.'));
    } finally {
      setRunning(false);
    }
  };

  const runNow = async () => {
    setProblem(null);
    setRunning(true);
    try {
      const res = await autoBlock.run(true);
      setCandidates(Array.isArray(res.data?.candidates) ? res.data.candidates : []);
      const results = Array.isArray(res.data?.results) ? res.data.results : [];
      const failures = results.filter((r) => !r.success);
      setRunMessage(res.data?.message || 'Feito.');
      if (failures.length > 0) {
        setProblem(`${failures.length} instância(s) não foram alteradas: `
          + failures.map((f) => `${f.instanceName} (${f.message})`).join('; '));
      }
      await load();
    } catch (e) {
      setProblem(apiMessage(e, 'Não foi possível aplicar o bloqueio.'));
    } finally {
      setRunning(false);
      setConfirming(false);
    }
  };

  const fromConfig = state?.fromConfig !== false;
  const actsNow = enabled && !simulateOnly;
  const toBlock = candidates.filter((c) => c.action === 'block');
  const toReactivate = candidates.filter((c) => c.action === 'reactivate');
  const excluded = candidates.filter((c) => c.action === 'excluded');

  return (
    <div data-testid="autoblock-panel" style={styles.card}>
      <div style={styles.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: 0 }}>
          <ShieldAlert size={18} color="var(--accent-primary)" style={{ flexShrink: 0 }} />
          <h3 style={styles.title}>Bloqueio automático por vencimento</h3>
        </div>
        <span data-testid="autoblock-state" style={{ ...styles.chip, ...(actsNow ? styles.chipOn : styles.chipOff) }}>
          {actsNow ? 'Ligado — bloqueia' : enabled ? 'Ligado — só simula' : 'Desligado'}
        </span>
      </div>

      {loading && (
        <div style={styles.muted}>
          <Loader2 size={16} style={{ animation: 'su-spin 1s linear infinite' }} /> A ler as definições…
        </div>
      )}

      {!loading && loadError && (
        <div data-testid="autoblock-error" style={styles.errorBox}>
          <div>{loadError}</div>
          <button type="button" onClick={load} style={{ ...styles.secondary, marginTop: '0.5rem' }}>
            Tentar de novo
          </button>
        </div>
      )}

      {!loading && !loadError && (
        <>
          <div data-testid="autoblock-rule" style={styles.rule}>
            <strong>A regra:</strong> a subscrição vence na data de faturação (ou no fim do trial) e a
            instância é bloqueada depois de <strong>{graceDays} dia{graceDays === 1 ? '' : 's'}</strong> de
            carência — no {graceDays + 1}.º dia de atraso. Carência por omissão:{' '}
            <strong>{state?.defaults?.graceDays ?? 7} dias</strong>{' '}
            (<code style={styles.code}>Billing:AutoBlock:GraceDays</code>).
            {' '}A plataforma (<code style={styles.code}>superuser</code>) e as instâncias inactivas
            nunca são bloqueadas, e um bloqueio feito à mão nunca é desfeito por este trabalho.
          </div>

          <label style={styles.switchRow} htmlFor="autoblock-enabled">
            <input
              id="autoblock-enabled"
              data-testid="autoblock-enabled"
              type="checkbox"
              checked={enabled}
              onChange={(e) => { setEnabled(e.target.checked); setMessage(null); setProblem(null); }}
              style={styles.checkbox}
            />
            <span style={styles.switchLabel}>Ligar o bloqueio automático</span>
          </label>

          <label style={styles.switchRow} htmlFor="autoblock-simulate">
            <input
              id="autoblock-simulate"
              data-testid="autoblock-simulate"
              type="checkbox"
              checked={simulateOnly}
              onChange={(e) => { setSimulateOnly(e.target.checked); setMessage(null); setProblem(null); }}
              style={styles.checkbox}
            />
            <span style={styles.switchLabel}>
              Só simular (não bloqueia ninguém)
              <span style={styles.hint}>
                Ligado por omissão. Para bloquear de verdade, desligue esta opção — e só depois de ver
                a lista de quem seria bloqueado hoje.
              </span>
            </span>
          </label>

          <label htmlFor="autoblock-grace" style={styles.label}>Carência (dias)</label>
          <input
            id="autoblock-grace"
            data-testid="autoblock-grace"
            type="number"
            min="0"
            max="365"
            inputMode="numeric"
            value={graceDays}
            onChange={(e) => { setGraceDays(parseInt(e.target.value, 10) || 0); setMessage(null); setProblem(null); }}
            style={{ ...styles.input, maxWidth: '8rem' }}
          />

          <div style={styles.actions}>
            <button
              type="button"
              data-testid="autoblock-save"
              onClick={save}
              disabled={saving}
              style={{ ...styles.primary, opacity: saving ? 0.6 : 1 }}
            >
              {saving ? 'A guardar…' : 'Guardar'}
            </button>
            <button
              type="button"
              data-testid="autoblock-preview"
              onClick={preview}
              disabled={running}
              style={styles.secondary}
            >
              {running ? 'A simular…' : 'Simular agora'}
            </button>
            {actsNow && (
              <button
                type="button"
                data-testid="autoblock-apply"
                onClick={() => setConfirming(true)}
                disabled={running || confirming}
                style={{ ...styles.danger, opacity: running ? 0.6 : 1 }}
              >
                Aplicar agora
              </button>
            )}
          </div>

          {confirming && (
            <div data-testid="autoblock-confirm-box" style={styles.warnBox}>
              <div>
                Vai bloquear <strong>{toBlock.length}</strong> instância(s) fora da carência e reabrir{' '}
                <strong>{toReactivate.length}</strong>. Nada é apagado: a instância fica inactiva e o
                histórico registra o motivo.
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
                <button
                  type="button"
                  data-testid="autoblock-confirm"
                  onClick={runNow}
                  disabled={running}
                  style={styles.danger}
                >
                  {running ? 'A aplicar…' : 'Confirmar bloqueio'}
                </button>
                <button type="button" data-testid="autoblock-cancel" onClick={() => setConfirming(false)} style={styles.secondary}>
                  Cancelar
                </button>
              </div>
            </div>
          )}

          {message && <div data-testid="autoblock-message" style={styles.okBox}>{message}</div>}
          {runMessage && <div data-testid="autoblock-run-message" style={styles.okBox}>{runMessage}</div>}
          {problem && <div data-testid="autoblock-problem" style={styles.errorBox}>{problem}</div>}

          <div style={styles.subhead}>
            Quem seria bloqueado hoje
            {!fromConfig && <span style={styles.badge}>definição gravada</span>}
            {fromConfig && <span style={styles.badge}>omissão do servidor</span>}
          </div>

          {toBlock.length === 0 && toReactivate.length === 0 && (
            <div data-testid="autoblock-none" style={styles.empty}>
              Nenhuma instância fora da carência. Nada a fazer hoje.
            </div>
          )}

          {toBlock.length > 0 && (
            <ul data-testid="autoblock-candidates" style={styles.list}>
              {toBlock.map((c) => (
                <li key={`b-${c.instanceId}`} data-testid="autoblock-candidate" style={styles.item}>
                  <div style={styles.itemHead}>
                    <strong>{c.instanceName}</strong>
                    <span style={styles.when}>{c.daysOverdue} dias de atraso</span>
                  </div>
                  <div style={styles.meta}>Plano {c.planName || '—'} · venceu a {fmtDate(c.dueDate)}</div>
                  <div style={styles.reasonLine}>{c.reason}</div>
                </li>
              ))}
            </ul>
          )}

          {toReactivate.length > 0 && (
            <ul data-testid="autoblock-reactivations" style={styles.list}>
              {toReactivate.map((c) => (
                <li key={`r-${c.instanceId}`} data-testid="autoblock-reactivation" style={styles.item}>
                  <div style={styles.itemHead}>
                    <strong>{c.instanceName}</strong>
                    <span style={{ ...styles.when, color: 'var(--success, #146c2e)' }}>reabrir</span>
                  </div>
                  <div style={styles.reasonLine}>{c.reason}</div>
                </li>
              ))}
            </ul>
          )}

          {excluded.length > 0 && (
            <div data-testid="autoblock-excluded" style={styles.excludedBox}>
              <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>Fora do alcance, de propósito:</div>
              {excluded.map((c) => (
                <div key={`x-${c.instanceId}`} data-testid="autoblock-excluded-item" style={styles.excludedItem}>
                  <strong>{c.instanceName}</strong> — {c.reason}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const styles = {
  card: {
    background: 'var(--bg-secondary, #fff)',
    border: '1px solid var(--border, #e3e3e3)',
    borderRadius: '16px',
    padding: '1rem',
    marginTop: '1rem',
    color: 'var(--text-primary, #1c1c1c)',
  },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' },
  title: { margin: 0, fontSize: '1rem', fontWeight: 700 },
  chip: { padding: '2px 8px', borderRadius: '999px', fontSize: '0.75rem', fontWeight: 700 },
  chipOn: { background: 'var(--success-bg, #e7f6ec)', color: 'var(--success-text, #146c2e)' },
  chipOff: { background: 'var(--bg-tertiary, #f1f1f1)', color: 'var(--text-muted, #6b6b6b)' },
  muted: {
    marginTop: '0.75rem', fontSize: '0.8125rem', color: 'var(--text-muted, #6b6b6b)',
    display: 'flex', alignItems: 'center', gap: '0.4rem',
  },
  rule: {
    marginTop: '0.75rem', padding: '0.75rem', borderRadius: '10px', fontSize: '0.8125rem',
    background: 'var(--bg-tertiary, #f7f7f7)', color: 'var(--text-primary, #1c1c1c)', lineHeight: 1.6,
  },
  switchRow: {
    display: 'flex', alignItems: 'flex-start', gap: '0.5rem', marginTop: '0.75rem',
    minHeight: TAP, cursor: 'pointer',
  },
  checkbox: { width: '22px', height: '22px', marginTop: '0.55rem', flexShrink: 0, accentColor: 'var(--accent-primary, #d4a017)' },
  switchLabel: { fontSize: '0.875rem', fontWeight: 600, display: 'flex', flexDirection: 'column', gap: '0.15rem' },
  hint: { fontSize: '0.75rem', fontWeight: 400, color: 'var(--text-muted, #6b6b6b)', lineHeight: 1.5 },
  label: { display: 'block', marginTop: '0.75rem', marginBottom: '0.35rem', fontSize: '0.8125rem', fontWeight: 600 },
  input: {
    minHeight: TAP, padding: '0 0.75rem', borderRadius: '10px', width: '100%', boxSizing: 'border-box',
    border: '1px solid var(--border, #e3e3e3)', background: 'var(--bg-tertiary, #f7f7f7)',
    color: 'var(--text-primary, #1c1c1c)', fontSize: '1rem',
  },
  actions: { display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.75rem' },
  primary: {
    minHeight: TAP, padding: '0 1rem', borderRadius: '10px', border: 'none',
    background: 'var(--accent-primary, #d4a017)', color: '#fff', fontWeight: 700, fontSize: '0.875rem',
    cursor: 'pointer',
  },
  secondary: {
    minHeight: TAP, minWidth: TAP, padding: '0 1rem', borderRadius: '10px',
    border: '1px solid var(--border, #e3e3e3)', background: 'transparent',
    color: 'var(--text-primary, #1c1c1c)', fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer',
  },
  danger: {
    minHeight: TAP, padding: '0 1rem', borderRadius: '10px', border: 'none',
    background: 'var(--error-bg, #fdeaea)', color: 'var(--error-text, #a11616)',
    fontWeight: 700, fontSize: '0.875rem', cursor: 'pointer',
  },
  warnBox: {
    marginTop: '0.75rem', padding: '0.75rem', borderRadius: '10px', fontSize: '0.8125rem',
    background: 'var(--warning-bg, #fff7e0)', color: 'var(--warning-text, #7a5200)', lineHeight: 1.6,
  },
  okBox: {
    marginTop: '0.75rem', padding: '0.75rem', borderRadius: '10px', fontSize: '0.8125rem',
    background: 'var(--success-bg, #e7f6ec)', color: 'var(--success-text, #146c2e)',
  },
  errorBox: {
    marginTop: '0.75rem', padding: '0.75rem', borderRadius: '10px', fontSize: '0.8125rem',
    background: 'var(--error-bg, #fdeaea)', color: 'var(--error-text, #a11616)', lineHeight: 1.5,
  },
  subhead: {
    marginTop: '1rem', fontSize: '0.8125rem', fontWeight: 700,
    display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap',
  },
  badge: {
    fontSize: '0.75rem', fontWeight: 600, padding: '1px 8px', borderRadius: '999px',
    background: 'var(--bg-tertiary, #f1f1f1)', color: 'var(--text-muted, #6b6b6b)',
  },
  empty: {
    marginTop: '0.5rem', padding: '0.75rem', borderRadius: '10px', fontSize: '0.8125rem',
    background: 'var(--bg-tertiary, #f7f7f7)', color: 'var(--text-muted, #6b6b6b)', lineHeight: 1.6,
  },
  list: { listStyle: 'none', margin: '0.5rem 0 0', padding: 0 },
  item: { padding: '0.625rem 0', borderTop: '1px solid var(--bg-tertiary, #f1f1f1)', fontSize: '0.8125rem' },
  itemHead: { display: 'flex', justifyContent: 'space-between', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' },
  when: { fontSize: '0.75rem', color: 'var(--text-muted, #6b6b6b)' },
  meta: { marginTop: '0.15rem', fontSize: '0.75rem', color: 'var(--text-muted, #6b6b6b)' },
  reasonLine: { marginTop: '0.15rem', fontSize: '0.75rem', fontStyle: 'italic', lineHeight: 1.5 },
  excludedBox: {
    marginTop: '0.75rem', padding: '0.75rem', borderRadius: '10px', fontSize: '0.75rem',
    background: 'var(--bg-tertiary, #f7f7f7)', color: 'var(--text-muted, #6b6b6b)', lineHeight: 1.6,
  },
  excludedItem: { marginTop: '0.2rem' },
  code: { fontFamily: 'monospace', fontSize: '0.75rem' },
};

export default AutoBlockPanel;
